import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { baseURL } from "../../../../api/api";
import { getCampaignById } from "../../../../api/campaigns.api";
import CompanyLayout from "../../../layout/CompanyLayout";
import { UserContext } from "../../../../context/UserContext";
import {
  FiPlus,
  FiWifi,
  FiWifiOff,
  FiMonitor,
  FiTruck,
  FiCoffee,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";

function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getCampaignById(id)
      .then((res) => {
        setCampaignDetails(res?.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getScreenTypeIcon = (type) => {
    switch (type) {
      case "vehicle":
        return <FiTruck className="text-yellow text-lg" />;
      case "restaurant":
        return <FiCoffee className="text-amber-500 text-lg" />;
      default:
        return <FiMonitor className="text-gray-500 text-lg" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "online":
        return (
          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
            <FiWifi className="text-green-500" /> Online
          </span>
        );
      case "offline":
        return (
          <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
            <FiWifiOff className="text-red-500" /> Offline
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
            <FiWifiOff className="text-gray-500" /> Unknown
          </span>
        );
    }
  };

  return (
    <CompanyLayout title="Campaign Details" ownerDetails={user}>
      <div className="mb-5 flex items-center justify-between sticky top-16 bg-slate-50 py-3 z-10 px-4">
        <h2 className="md:text-2xl font-bold text-gray-800 truncate max-w-[70%] md:ml-7">
          {campaignDetails?.name || "Campaign Details"}
        </h2>
        <Link
          to={`/campaigns/edit/${id}`}
          className="flex items-center justify-center rounded-md bg-yellow px-4 py-2.5 text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor transition-all duration-200 hover:shadow-md whitespace-nowrap"
        >
          <FiPlus className="text-lg text-gray-700 mr-2" />
          Edit Campaign
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : (
        <div className="px-4 md:px-10 gap-6 grid grid-cols-1 md:grid-cols-12">
          {/* Left Column - Campaign Info */}
          <div className="md:col-span-8 col-span-1 space-y-6">
            {/* Campaign Summary Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-gray-50 min-w-0">
                  <label className="block text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <p className="mt-1 text-lg font-semibold text-gray-800 truncate">
                    {campaignDetails?.name}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50 min-w-0">
                  <label className="block text-sm font-medium text-gray-500">
                    Location
                  </label>
                  <p className="mt-1 text-lg font-semibold text-gray-800 truncate">
                    {campaignDetails?.location || "Not specified"}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50 min-w-0">
                  <label className="block text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <p className="mt-1 text-lg font-semibold text-gray-800">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        campaignDetails?.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : campaignDetails?.status === "Pending"
                          ? "bg-yellow text-black"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {campaignDetails?.status}
                    </span>
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50 min-w-0">
                  <label className="block text-sm font-medium text-gray-500">
                    Company
                  </label>
                  <p className="mt-1 text-lg font-semibold text-gray-800 truncate">
                    {campaignDetails?.company?.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Campaign Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Dates Card */}
              <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Timeline
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Start Date
                    </label>
                    <p className="mt-1 text-base font-medium text-gray-800">
                      {formatDate(campaignDetails?.startDate)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      End Date
                    </label>
                    <p className="mt-1 text-base font-medium text-gray-800">
                      {formatDate(campaignDetails?.endDate)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Duration
                    </label>
                    <p className="mt-1 text-base font-medium text-gray-800">
                      {campaignDetails?.numberOfWeeks} week(s)
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Statistics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-yellow">
                    <label className="block text-sm font-medium text-black">
                      Screens
                    </label>
                    <p className="mt-1 text-2xl font-bold text-black">
                      {campaignDetails?.numberOfScreensRunning || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50">
                    <label className="block text-sm font-medium text-black">
                      Impressions
                    </label>
                    <p className="mt-1 text-2xl font-bold text-black">
                      {campaignDetails?.impressions || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-100">
                    <label className="block text-sm font-medium text-black">
                      Total Cost
                    </label>
                    <p className="mt-1 text-2xl font-bold text-black">
                      €{campaignDetails?.totalCost?.toFixed(2) || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-50">
                    <label className="block text-sm font-medium text-gblack">
                      Status
                    </label>
                    <p className="mt-1 text-xl font-bold text-black">
                      {campaignDetails?.paid ? "Paid" : "Unpaid"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Screens Section */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Screens Running This Campaign
                </h3>
                <span className="bg-yellow text-black text-xs font-medium px-2.5 py-1 rounded-full">
                  {campaignDetails?.numberOfScreensRunning || 0} screens
                </span>
              </div>

              {campaignDetails?.screensRunningOn?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {campaignDetails.screensRunningOn.map((screen) => (
                    <div
                      key={screen._id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer hover:border-blue-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                            {getScreenTypeIcon(screen.screenType)}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-medium text-gray-800 truncate">
                              {screen.model}
                            </h4>
                            <p className="text-xs text-gray-500 truncate">
                              {screen.uuid}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(screen.onlineStatus)}
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">IMEI:</span>
                          <span className="font-medium text-gray-700 truncate">
                            {screen.imeiNumber}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-gray-500">Type:</span>
                          <span className="font-medium text-gray-700 capitalize">
                            {screen.screenType}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto h-24 w-24 text-gray-400">
                    <FiMonitor className="w-full h-full" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No screens found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This campaign is not running on any screens yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Video and Details */}
          <div className="md:col-span-4 col-span-1 space-y-6">
            {/* Video Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800">
                  Campaign Video
                </h3>
              </div>
              <div className="aspect-w-16 aspect-h-9">
                <video
                  src={`${baseURL}/s3/images/${campaignDetails?.video?.name}`}
                  alt="Campaign Video"
                  className="w-full h-full object-cover"
                  controls
                  muted
                />
              </div>
              <div className="p-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">
                    Video Details
                  </span>
                  <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                    {campaignDetails?.video?.type}
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium text-gray-800 truncate">
                  {campaignDetails?.video?.name}
                </p>
              </div>
            </div>

            {/* Campaign Type Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Campaign Type
              </h3>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow rounded-lg text-yellow-400 flex-shrink-0">
                  {campaignDetails?.type === "vehicle" ? (
                    <FiTruck className="text-2xl" />
                  ) : campaignDetails?.type === "restaurant" ? (
                    <FiCoffee className="text-2xl" />
                  ) : (
                    <FiMonitor className="text-2xl" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-500">Type</p>
                  <p className="text-lg font-semibold text-gray-800 capitalize truncate">
                    {campaignDetails?.type}
                  </p>
                </div>
              </div>
            </div>

            {/* Created By Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Created By
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 text-gray-600 font-semibold flex-shrink-0">
                  {campaignDetails?.createdBy?.firstName?.charAt(0)}
                  {campaignDetails?.createdBy?.lastName?.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {campaignDetails?.createdBy?.firstName}{" "}
                    {campaignDetails?.createdBy?.lastName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {campaignDetails?.createdBy?.email}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
}

export default CampaignDetails;
